import React, { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LocaleProvider } from './context/LocaleContext';
import HomePage from './pages/HomePage';
import CityDirectoryPage from './pages/CityDirectoryPage';
import CountryDirectoryPage from './pages/CountryDirectoryPage';
import { getPreferredLocale, isSupportedLocale } from './utils/lang-route';
import './styles/globals.css';

const RedirectToPreferredLocale: React.FC = () => {
  const locale = getPreferredLocale();
  return <Navigate to={`/${locale}`} replace />;
};

const RedirectLegacyCountryRoute: React.FC = () => {
  const { pathname, search } = useLocation();
  const m = pathname.match(/^\/country\/([^/]+)$/);
  if (!m) {
    return <RedirectToPreferredLocale />;
  }
  const locale = getPreferredLocale();
  return <Navigate to={`/${locale}/country/${m[1]}${search || ''}`} replace />;
};

const GuardedLocale: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const lang = pathname.split('/').filter(Boolean)[0];
  if (!isSupportedLocale(lang)) {
    return <RedirectToPreferredLocale />;
  }
  return <>{children}</>;
};

const RouteSeoSync: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const canonicalUrl = `${window.location.origin}${pathname}`;

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', canonicalUrl);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', canonicalUrl);
    }

    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', canonicalUrl);
    }
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  return (
    <LocaleProvider>
      <Router>
        <RouteSeoSync />
        <Routes>
          <Route path="/" element={<RedirectToPreferredLocale />} />
          <Route path="/country/:id" element={<RedirectLegacyCountryRoute />} />
          <Route
            path="/:lang"
            element={
              <GuardedLocale>
                <HomePage />
              </GuardedLocale>
            }
          />
          <Route
            path="/:lang/country/:id/city/:citySlug"
            element={
              <GuardedLocale>
                <CityDirectoryPage />
              </GuardedLocale>
            }
          />
          <Route
            path="/:lang/country/:id"
            element={
              <GuardedLocale>
                <CountryDirectoryPage />
              </GuardedLocale>
            }
          />
          <Route path="*" element={<RedirectToPreferredLocale />} />
        </Routes>
      </Router>
    </LocaleProvider>
  );
};

export default App; 